import React, { useState, useEffect, useRef } from "react";
// --- FIREBASE IMPORTS PRESERVED ---
import { doc, setDoc, getDoc, query, where, collection, getDocs, serverTimestamp, deleteDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "./firebase";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
  <input
    style={{
      width: "100%",
      padding: "10px 12px",
      borderRadius: "8px",
      border: "1px solid #eee",
      fontSize: "14px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
    }}
    onClick={onClick}
    ref={ref}
    value={value}
    readOnly
  />
));

// --- ICONS ---
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E07A5F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const XMarkIcon = ({ size = 14, color = "#555" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const PlayIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="rgba(0,0,0,0.6)" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill="rgba(255,255,255,0.8)" stroke="none"></circle>
    <polygon points="10 8 16 12 10 16 10 8" fill="#333"></polygon>
  </svg>
);

const KeyboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" ry="2"></rect>
    <line x1="6" y1="8" x2="6" y2="8"></line>
    <line x1="10" y1="8" x2="10" y2="8"></line>
    <line x1="14" y1="8" x2="14" y2="8"></line>
    <line x1="18" y1="8" x2="18" y2="8"></line>
    <line x1="6" y1="12" x2="6" y2="12"></line>
    <line x1="10" y1="12" x2="10" y2="12"></line>
    <line x1="14" y1="12" x2="14" y2="12"></line>
    <line x1="18" y1="12" x2="18" y2="12"></line>
    <line x1="6" y1="16" x2="18" y2="16"></line>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "#F4A261" : "none"} stroke="#F4A261" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const EditPencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const CalendarIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const UserIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const PackageIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

// --- STYLES ---
const styles = {
  page: { backgroundColor: "#FFF7F1", minHeight: "100vh", fontFamily: "'Syne', sans-serif", padding: "20px", paddingBottom: "100px" },
  header: { display: "flex", alignItems: "center", justifyContent: 'center', position: 'relative', marginBottom: "30px", paddingTop: "10px" },
  headerTitle: { fontSize: "20px", fontWeight: "normal", color: "#222", margin: 0 },
  backBtn: { background: "none", border: "none", cursor: "pointer", padding: "5px", position: 'absolute', left: 0 },
  closeBtn: { background: "#FADCD2", border: "none", borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: "pointer", position: 'absolute', right: 0 },

  // Cards & Inputs
  card: { backgroundColor: "transparent", marginBottom: "20px" },
  label: { display: "block", fontSize: "14px", fontWeight: "700", marginBottom: "8px", color: "#111", marginTop: 10 },
  input: { width: "100%", padding: "16px", borderRadius: "12px", border: "none", fontSize: "15px", marginBottom: "20px", outline: "none", boxSizing: "border-box", backgroundColor: '#FFF', boxShadow: "0 2px 5px rgba(0,0,0,0.02)", color: '#333' },
  textArea: { width: "100%", padding: "16px", borderRadius: "12px", border: "none", fontSize: "15px", minHeight: "100px", marginBottom: "20px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", backgroundColor: '#FFF', boxShadow: "0 2px 5px rgba(0,0,0,0.02)", lineHeight: "1.5", color: '#333' },
  btn: { width: "100%", padding: "16px", backgroundColor: "#222", color: "white", border: "none", borderRadius: "16px", fontWeight: "bold", cursor: "pointer", fontSize: "16px", boxShadow: "0 5px 15px rgba(0,0,0,0.1)" },

  // Specialisation Input Group
  specInputWrapper: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' },
  plusBtn: { minWidth: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#FADCD2', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#555' },

  // Tag Styles
  tagContainer: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  tag: { display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#FADCD2', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', color: '#555', fontWeight: '500' },

  // Initials Avatar
  avatarContainer: { display: 'flex', justifyContent: 'center', marginBottom: '30px' },
  initialsAvatar: { width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', fontWeight: 'bold', color: '#333', boxShadow: '0 5px 15px rgba(0,0,0,0.1)', border: '4px solid white' },

  // Availability
  dayCircleContainer: { display: 'flex', justifyContent: 'space-between', marginBottom: '25px', gap: '5px' },
  dayCircle: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', backgroundColor: '#AED9C8', color: '#333' },
  dayCircleActive: { backgroundColor: '#8BC34A', color: 'white', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)', transform: 'scale(1.1)' },
  //   timeSlotContainer: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  //   addSlotPill: { display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 20px', borderRadius: '25px', backgroundColor: '#FFF', color: '#333', border: '1px solid #eee', fontSize: '14px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' },
  //   slotPill: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 15px', borderRadius: '25px', backgroundColor: '#FADCD2', color: '#333', fontSize: '14px', fontWeight: '500' },
  datePicker: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #eee",
    fontSize: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  // timeSlotContainer: { display: "flex", flexWrap: "wrap", gap: "10px" }, 
  timeSlotContainer: {
    display: "flex",
    flexDirection: "column", // Stack inputs vertically for cleaner layout
    gap: "12px",
    padding: "12px 0",
  },
  // addSlotPill: {
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "5px",
  //       padding: "10px 20px",
  //       borderRadius: "25px",
  //       backgroundColor: "#FFF",
  //       color: "#333",
  //       border: "1px solid #eee",
  //       fontSize: "14px",
  //       fontWeight: "500",
  //       cursor: "pointer",
  //       boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
  //     },
  addSlotPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    padding: "8px 16px",
    borderRadius: "30px",
    backgroundColor: "#FFF",
    color: "#333",
    border: "1px solid #eee",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
    transition: "transform 0.15s, box-shadow 0.15s",
    minWidth: "120px"
  },
  addSlotPillHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 5px 12px rgba(0,0,0,0.12)",
  },
  slotPill: {
    display: "inline-flex",
    flexDirection: 'row',
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "8px 15px",
    borderRadius: "25px",
    backgroundColor: "#FADCD2",
    color: "#333",
    fontSize: "13px",
    fontWeight: "500",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    margin: "4px"
  },
  // slotPill: {
  //       display: "flex",
  //       alignItems: "center",
  //       gap: "10px",
  //       padding: "10px 15px",
  //       borderRadius: "25px",
  //       backgroundColor: "#FADCD2",
  //       color: "#333",
  //       fontSize: "14px",
  //       fontWeight: "500",
  //     },
  // Video Upload
  uploadZone: { border: '2px dashed #DCC', borderRadius: '12px', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF', cursor: 'pointer', marginBottom: '20px' },
  videoPreview: { position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#000', height: '200px' },

  emptyState: { textAlign: "center", color: "#888", marginTop: "50px", fontStyle: "italic" },

  // HISTORY SPECIFIC
  historyCard: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(197, 123, 87, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  historyCardHeader: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  historyAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    backgroundColor: '#F3E5DC',
    color: '#C57B57',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif"
  },
  historyClientName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#003049',
    margin: 0,
    fontFamily: "'Syne', sans-serif"
  },
  historyMetaRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '4px'
  },
  historyMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#666'
  },
  historyDetailsGrid: {
    display: 'flex',
    gap: '20px',
    padding: '12px 0',
    borderTop: '1px solid #F1F2F6',
    borderBottom: '1px solid #F1F2F6'
  },
  historyDetailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#444'
  },
  historyNotesSection: {
    backgroundColor: '#FFF7F1',
    padding: '16px',
    borderRadius: '16px',
    borderLeft: '4px solid #C57B57'
  },
  historyTabsWrapper: {
    display: 'flex',
    gap: '12px',
    borderBottom: '1px solid #E2E8F0',
    marginBottom: '4px',
    overflowX: 'auto',
    paddingBottom: '2px'
  },
  historyTabBtn: {
    background: 'none',
    border: 'none',
    padding: '8px 4px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  },
  historyNotesLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#A0AEC0',
    letterSpacing: '0.5px',
    marginBottom: '8px'
  },
  historyNotesText: {
    fontSize: '13px',
    color: '#444',
    lineHeight: '1.6',
    fontStyle: 'italic'
  },
  emptyHistory: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#A0AEC0',
    fontSize: '14px'
  },

  // Logout Button
  logoutBtn: { width: '100%', padding: '16px', backgroundColor: '#F07167', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 5px 15px rgba(240, 113, 103, 0.3)' }
};

// --- CUSTOM CLOCK COMPONENT ---
const AnalogTimePicker = ({ isOpen, onClose, onSave }) => {
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(13);
  const [period, setPeriod] = useState("PM"); // AM/PM
  const [mode, setMode] = useState("hours"); // 'hours' or 'minutes'
  const [inputMethod, setInputMethod] = useState("clock"); // 'clock' or 'keyboard'
  const [manualTime, setManualTime] = useState("");


  if (!isOpen) return null;

  const hoursArray = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutesArray = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const getPosition = (index, radius = 95) => {
    const angle = (index * 30 - 90) * (Math.PI / 180);
    const x = 130 + radius * Math.cos(angle);
    const y = 130 + radius * Math.sin(angle);
    return { left: x, top: y };
  };

  const getHandRotation = () => {
    if (mode === "hours") return (hour % 12) * 30;
    else return minute * 6;
  };

  const handleClockClick = (value) => {
    if (mode === "hours") {
      setHour(value === 0 ? 12 : value);
      setMode("minutes");
    } else {
      setMinute(value);
    }
  };

  const handleManualSave = () => {
    if (!manualTime) return;
    onSave(manualTime);
    onClose();
  };

  const handleClockSave = () => {
    const formattedHour = String(hour).padStart(2, '0');
    const formattedMinute = String(minute).padStart(2, '0');
    onSave(`${formattedHour}:${formattedMinute} ${period}`);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={{ backgroundColor: 'white', padding: '24px', borderRadius: '24px', width: '320px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <div style={{ marginBottom: '20px', textAlign: 'left', width: '100%' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#666', fontWeight: '500' }}>Pick the starting time for a one hour slot</p>
        </div>

        {inputMethod === "clock" ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
            <div onClick={() => setMode("hours")} style={{ fontSize: '56px', fontWeight: '400', color: mode === 'hours' ? '#111' : '#999', cursor: 'pointer' }}>{String(hour)}</div>
            <span style={{ fontSize: '50px', position: 'relative', top: '-5px', color: '#333' }}>:</span>
            <div onClick={() => setMode("minutes")} style={{ fontSize: '56px', fontWeight: '400', color: mode === 'minutes' ? '#111' : '#999', cursor: 'pointer' }}>{String(minute).padStart(2, '0')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginLeft: '10px' }}>
              <div onClick={() => setPeriod("AM")} style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: period === "AM" ? '#FFDFC4' : 'white' }}>AM</div>
              <div onClick={() => setPeriod("PM")} style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: period === "PM" ? '#FFDFC4' : 'white', borderTop: '1px solid #ddd' }}>PM</div>
            </div>
          </div>
        ) : (
          <div style={{ width: '100%', marginBottom: '30px' }}>
            <input autoFocus type="time" style={{ width: '100%', padding: '15px', fontSize: '24px', textAlign: 'center', borderRadius: '12px', border: '1px solid #ddd' }} onChange={(e) => setManualTime(e.target.value)} />
          </div>
        )}

        {inputMethod === "clock" && (
          <div style={{ width: '260px', height: '260px', position: 'relative', borderRadius: '50%', backgroundColor: '#f5f5f5', marginBottom: '20px' }}>
            <div style={{ position: 'absolute', width: '8px', height: '8px', backgroundColor: '#F4A261', borderRadius: '50%', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 5 }}></div>
            <div style={{ position: 'absolute', top: '50%', left: '50%', width: '2px', height: '90px', backgroundColor: '#F4A261', transformOrigin: '50% 0%', transform: `translate(-50%, 0) rotate(${getHandRotation() - 180}deg)`, zIndex: 1 }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(244, 162, 97, 0.4)', position: 'absolute', bottom: '-16px', left: '-15px' }}></div>
            </div>
            {mode === "hours" ? hoursArray.map((h, i) => {
              const pos = getPosition(i);
              const isSelected = hour === h || (h === 12 && hour === 0);
              return <div key={h} onClick={() => handleClockClick(h)} style={{ position: 'absolute', left: pos.left, top: pos.top, transform: 'translate(-50%, -50%)', fontSize: '16px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#fff' : '#333', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isSelected ? '#F4A261' : 'transparent', borderRadius: '50%', zIndex: 2 }}>{h}</div>;
            }) : minutesArray.map((m, i) => {
              const pos = getPosition(i);
              const isSelected = minute === m;
              return <div key={m} onClick={() => handleClockClick(m)} style={{ position: 'absolute', left: pos.left, top: pos.top, transform: 'translate(-50%, -50%)', fontSize: '14px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#fff' : '#333', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isSelected ? '#F4A261' : 'transparent', borderRadius: '50%', zIndex: 2 }}>{String(m).padStart(2, '0')}</div>;
            })}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '10px', alignItems: 'center' }}>
          <div onClick={() => setInputMethod(inputMethod === "clock" ? "keyboard" : "clock")} style={{ cursor: 'pointer', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            {inputMethod === "clock" ? <KeyboardIcon /> : <ClockIcon />}
          </div>
          <div style={{ display: 'flex', gap: '20px', fontSize: '15px', fontWeight: '500' }}>
            <span onClick={onClose} style={{ cursor: 'pointer', color: '#555' }}>Cancel</span>
            <span onClick={inputMethod === "clock" ? handleClockSave : handleManualSave} style={{ cursor: 'pointer', color: '#F4A261', fontWeight: 'bold' }}>OK</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- 0. VIEW PROFILE (UPDATED) ---
export const ViewProfile = ({ user, onBack, onEdit, onLogout, isDrawer = false }) => {
  const initials = user.displayName ? user.displayName.split(' ').map(n => n[0]).join('') : "TP";

  // Adjust header style if inside drawer vs full page
  const headerStyle = isDrawer ? { ...styles.header, justifyContent: 'space-between', paddingTop: '0' } : { ...styles.header, justifyContent: 'space-between' };

  return (
    <div style={styles.page}>
      <div style={headerStyle}>
        {isDrawer ? (
          <button onClick={onBack} style={{ ...styles.backBtn, position: 'static' }}><CloseIcon /></button>
        ) : (
          <button onClick={onBack} style={{ ...styles.backBtn, position: 'static' }}><ArrowLeftIcon /></button>
        )}
        <h2 style={styles.headerTitle}>My Profile</h2>
        <div style={{ width: 24 }}></div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ ...styles.initialsAvatar, backgroundColor: '#F4A261', color: 'white', marginBottom: '15px', overflow: 'hidden' }}>
          {user.image ? (
            <img src={user.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Profile" />
          ) : (
            initials
          )}
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#222', margin: '0 0 5px 0' }}>{user.displayName}</h1>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Clinical Psychologist</p>
      </div>

      <div style={{ backgroundColor: "white", borderRadius: "24px", padding: "25px", marginBottom: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)" }}>
        <div style={{ marginBottom: '20px', borderBottom: '1px solid #f0f0f0', paddingBottom: '15px' }}>
          <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', marginBottom: '5px' }}>Bio</div>
          <div style={{ fontSize: '16px', color: '#333', lineHeight: '1.5' }}>{user.bio || "No bio added yet."}</div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', marginBottom: '5px' }}>Qualification</div>
          <div style={{ fontSize: '16px', color: '#333' }}>{user.qualification || "Not specified"}</div>
        </div>
      </div>

      <div style={{ position: 'fixed', bottom: '100px', right: '30px', width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#222', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', cursor: 'pointer', zIndex: 100 }} onClick={onEdit}>
        <EditPencilIcon />
      </div>

      {/* LOGOUT BUTTON */}
      <button onClick={onLogout} style={styles.logoutBtn}>
        <LogoutIcon />
        Log Out
      </button>
    </div>
  );
};

// --- 1. EDIT PROFILE (UPDATED) ---
export const EditProfile = ({ user, onBack, onProfileUpdate }) => {
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || user?.name || "",
    qualification: user?.qualification || "",
    location: user?.location || "",
    bio: user?.bio || ""
  });

  const [tags, setTags] = useState(user?.expertise || []);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [videoFile, setVideoFile] = useState(user?.introVideo || null);
  const [videoRawFile, setVideoRawFile] = useState(null);
  const [profileImage, setProfileImage] = useState(user?.image || null);
  const [profileImageRawFile, setProfileImageRawFile] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const storage = getStorage();

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageRawFile(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (newTag.trim() !== "") {
      setTags(prev => [...prev, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoRawFile(file);
      const videoUrl = URL.createObjectURL(file);
      setVideoFile(videoUrl);
    }
  };

  // 5️⃣ Save to Firestore
  const handleSave = async () => {
    setLoading(true);

    if (!profileImage) {
      alert("Profile picture is mandatory. Please upload one.");
      setLoading(false);
      return;
    }

    try {
      if (!user?.uid) return;

      // 1. Upload Video if a new one was selected
      let finalVideoUrl = user?.introVideo || "";
      if (videoRawFile) {
        const videoRef = ref(storage, `therapist_videos/${Date.now()}_${videoRawFile.name}`);
        await uploadBytes(videoRef, videoRawFile);
        finalVideoUrl = await getDownloadURL(videoRef);
      }

      // 1b. Upload Profile Image if a new one was selected
      let finalImageUrl = profileImage || user?.image || "";
      if (profileImageRawFile) {
        const imageRef = ref(storage, `therapist_profiles/${Date.now()}_${profileImageRawFile.name}`);
        await uploadBytes(imageRef, profileImageRawFile);
        finalImageUrl = await getDownloadURL(imageRef);
      }

      // 2. Update therapist_availability (Basic Name & Availability)
      const availabilityRef = doc(db, "therapist_availability", user.uid);
      await setDoc(
        availabilityRef,
        {
          therapistName: profileData.displayName,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // 3. Update therapists collection (Bio, Qualification, Location, Video, Image)
      const therapistsRef = collection(db, "therapists");
      const q = query(therapistsRef, where("email", "==", user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const therapistDocRef = doc(db, "therapists", querySnapshot.docs[0].id);
        await setDoc(therapistDocRef, {
          bio: profileData.bio,
          qual: profileData.qualification,
          location: profileData.location,
          expertise: tags,
          introVideo: finalVideoUrl,
          image: finalImageUrl,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      alert("Changes updated to Firestore successfully!");
      setLoading(false);

      if (onProfileUpdate) {
        onProfileUpdate({
          ...profileData,
          introVideo: finalVideoUrl,
          image: finalImageUrl,
          expertise: tags
        });
      }

      onBack();
    }
    catch (error) {
      console.error("Save error:", error);
      alert("Failed to save changes: " + error.message);
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>Edit Profile</h2>
        <button onClick={onBack} style={styles.closeBtn}><CloseIcon /></button>
      </div>

      <div style={styles.avatarContainer}>
        <div style={{ position: 'relative' }}>
          <div
            style={{ ...styles.initialsAvatar, cursor: 'pointer', overflow: 'hidden' }}
            onClick={() => imageInputRef.current.click()}
            title="Click to change profile picture"
          >
            {profileImage ? (
              <img src={profileImage} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Profile" />
            ) : (
              (() => {
                const name = profileData.displayName || user?.displayName || user?.name || "Therapist";
                const parts = name.split(' ').filter(part => part.length > 0);
                if (parts.length === 1) {
                  return parts[0].charAt(0).toUpperCase();
                }
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
              })()
            )}
          </div>
          <div
            onClick={() => imageInputRef.current.click()}
            style={{
              position: 'absolute',
              bottom: '5px',
              right: '5px',
              backgroundColor: '#003049',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              cursor: 'pointer'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={imageInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
      </div>

      <div style={styles.card}>
        <label style={styles.label}>Address</label>
        <input style={styles.input} value={profileData.location} placeholder="Enter your address" onChange={(e) => handleProfileChange("location", e.target.value)} />

        <label style={styles.label}>Qualification</label>
        <input style={styles.input} value={profileData.qualification} placeholder="Enter your qualification" onChange={(e) => handleProfileChange("qualification", e.target.value)} />

        <label style={styles.label}>Bio</label>
        <textarea style={styles.textArea} value={profileData.bio} placeholder="Enter your bio" onChange={(e) => handleProfileChange("bio", e.target.value)} />
      </div>

      <div style={styles.card}>
        <label style={styles.label}>Specialisations</label>
        <div style={styles.specInputWrapper}>
          <input style={{ ...styles.input, marginBottom: 0 }} placeholder="Add specialisation" value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addTag()} />
          <button style={styles.plusBtn} onClick={addTag}><PlusIcon /></button>
        </div>
        <div style={styles.tagContainer}>
          {tags.map((tag, idx) => (
            <div key={idx} style={styles.tag}>
              {tag}
              <div onClick={() => removeTag(tag)} style={{ cursor: 'pointer', display: 'flex' }}><XMarkIcon size={12} color="#888" /></div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...styles.card, marginTop: '30px' }}>
        <label style={styles.label}>Intro Video</label>
        <input type="file" accept="video/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleVideoUpload} />
        {!videoFile ? (
          <div style={styles.uploadZone} onClick={() => fileInputRef.current.click()}>
            <UploadIcon />
            <span style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>Tap to upload intro video</span>
          </div>
        ) : (
          <div style={styles.videoPreview}>
            <video src={videoFile} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            <div onClick={() => { setVideoFile(null); setVideoRawFile(null); }} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', padding: '5px', borderRadius: '50%', cursor: 'pointer', zIndex: 10 }}><XMarkIcon color="white" /></div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px' }}>
        <button style={styles.btn} onClick={handleSave} disabled={loading}>{loading ? "Saving Changes..." : "Save Changes"}</button>
      </div>
    </div>
  );
};

// --- 1.5 AVAILABLE SLOTS ---
const CustomTimeInput = ({ label, time, setTime }) => {
  const get12HrFormat = (t) => {
    if (!t) return { h1: '0', h2: '9', m1: '0', m2: '0', ampm: 'AM' };
    const [hh, mm] = t.split(":");
    let h = parseInt(hh, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    const hStr = h.toString().padStart(2, '0');
    const mStr = (mm || "00").padStart(2, '0');
    return { h1: hStr[0], h2: hStr[1], m1: mStr[0], m2: mStr[1], ampm };
  };

  const [digits, setDigits] = useState(get12HrFormat(time));
  const inputsRef = useRef([]);

  useEffect(() => {
    setDigits(get12HrFormat(time));
  }, [time]);

  const updateParentTime = (newDigits) => {
    let h24 = parseInt(`${newDigits.h1}${newDigits.h2}`, 10) || 0;
    if (newDigits.ampm === "PM" && h24 !== 12) h24 += 12;
    if (newDigits.ampm === "AM" && h24 === 12) h24 = 0;

    setTime(`${h24.toString().padStart(2, '0')}:${newDigits.m1}${newDigits.m2}`);
  };

  const handleChange = (e, field, nextIndex) => {
    let val = e.target.value.replace(/\D/g, '');
    val = val.slice(-1);

    if (!val) {
      const newDigits = { ...digits, [field]: '0' };
      setDigits(newDigits);
      updateParentTime(newDigits);
      return;
    }

    const newDigits = { ...digits, [field]: val };

    if (field === 'h1' && parseInt(val, 10) > 1) {
      newDigits.h1 = '1';
    }
    if (field === 'm1' && parseInt(val, 10) > 5) {
      newDigits.m1 = '5';
    }

    setDigits(newDigits);
    updateParentTime(newDigits);

    if (nextIndex < 4 && inputsRef.current[nextIndex]) {
      inputsRef.current[nextIndex].focus();
      inputsRef.current[nextIndex].select();
    }
  };

  const handleKeyDown = (e, field, prevIndex) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = { ...digits, [field]: '0' };
      setDigits(newDigits);
      updateParentTime(newDigits);
      if (prevIndex >= 0 && inputsRef.current[prevIndex]) {
        inputsRef.current[prevIndex].focus();
        inputsRef.current[prevIndex].select();
      }
    }
  };

  const toggleAmpm = () => {
    const newDigits = { ...digits, ampm: digits.ampm === 'AM' ? 'PM' : 'AM' };
    setDigits(newDigits);
    updateParentTime(newDigits);
  };

  const boxStyle = {
    width: '32px',
    height: '40px',
    backgroundColor: '#FAECE5',
    borderRadius: '8px',
    border: 'none',
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#000',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    margin: '0 3px',
    outline: 'none',
    cursor: 'text',
    padding: 0
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px', width: '100%' }}>
      {label && <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#6F4F3D', marginBottom: '8px' }}>{label}</span>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <input
          ref={el => inputsRef.current[0] = el}
          type="text"
          value={digits.h1}
          onChange={(e) => handleChange(e, 'h1', 1)}
          onKeyDown={(e) => handleKeyDown(e, 'h1', -1)}
          onClick={(e) => e.target.select()}
          style={boxStyle}
        />
        <input
          ref={el => inputsRef.current[1] = el}
          type="text"
          value={digits.h2}
          onChange={(e) => handleChange(e, 'h2', 2)}
          onKeyDown={(e) => handleKeyDown(e, 'h2', 0)}
          onClick={(e) => e.target.select()}
          style={boxStyle}
        />
        <span style={{ fontWeight: 'bold', fontSize: '20px', margin: '0 5px' }}>:</span>
        <input
          ref={el => inputsRef.current[2] = el}
          type="text"
          value={digits.m1}
          onChange={(e) => handleChange(e, 'm1', 3)}
          onKeyDown={(e) => handleKeyDown(e, 'm1', 1)}
          onClick={(e) => e.target.select()}
          style={boxStyle}
        />
        <input
          ref={el => inputsRef.current[3] = el}
          type="text"
          value={digits.m2}
          onChange={(e) => handleChange(e, 'm2', 4)}
          onKeyDown={(e) => handleKeyDown(e, 'm2', 2)}
          onClick={(e) => e.target.select()}
          style={boxStyle}
        />
        <div
          onClick={toggleAmpm}
          style={{ fontWeight: 'bold', fontSize: '16px', color: '#A09C99', marginLeft: '10px', cursor: 'pointer', padding: '5px' }}
        >
          {digits.ampm}
        </div>
      </div>
    </div>
  );
};

export const AvailableSlots = ({ user, onBack }) => {
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [availability, setAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentSlots, setCurrentSlots] = useState([]);
  const [bookedDates, setBookedDates] = useState({});

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDayClassName = (date) => {
    const dateStr = formatDate(date);
    let classes = [];

    if (availability[dateStr] && availability[dateStr].length > 0) {
      classes.push("react-datepicker__day--has-availability");
    }

    if (bookedDates[dateStr]) {
      classes.push("react-datepicker__day--has-bookings");
    }

    return classes.join(" ");
  };

  useEffect(() => {
    if (!user?.uid) return;
    const fetchAvailabilityAndBookings = async () => {
      try {
        // Fetch availability
        const docRef = doc(db, "therapist_availability", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const monthlySlots = docSnap.data().monthlySlots || {};
          setAvailability(monthlySlots);
        } else {
          setAvailability({});
        }

        // Fetch bookings for this therapist
        const bookingsRef = collection(db, "bookings");
        const q = query(bookingsRef, where("therapistId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        
        let bDates = {};
        querySnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.status === "cancelled") return;

            if (data.date) bDates[data.date] = true;
            if (data.session_1 && data.session_1.date) bDates[data.session_1.date] = true;
            
            for (let i = 2; i <= 4; i++) {
                const sess = data[`session_${i}`];
                if (sess && sess.date) {
                    bDates[sess.date] = true;
                }
            }
        });
        setBookedDates(bDates);

      } catch (error) {
        console.error("Error fetching availability and bookings:", error);
      }
    };
    fetchAvailabilityAndBookings();
  }, [user?.uid]);

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = formatDate(selectedDate);
      setCurrentSlots(availability[formattedDate] || []);
    } else {
      setCurrentSlots([]);
    }
  }, [availability, selectedDate]);

  const removeTimeSlot = (slotToRemove) => {
    const formattedDate = formatDate(selectedDate);
    const updatedSlots = (availability[formattedDate] || []).filter(
      (slot) => slot.from !== slotToRemove.from || slot.to !== slotToRemove.to
    );
    const updatedAvailability = { ...availability, [formattedDate]: updatedSlots };
    setAvailability(updatedAvailability);
    setCurrentSlots(updatedSlots);
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hourStr, minute] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const addTimeSlot = () => {
    if (!startTime || !endTime || !selectedDate || !user?.uid) return;
    const formattedDate = formatDate(selectedDate);
    const newSlot = { from: formatTime12Hour(startTime), to: formatTime12Hour(endTime) };
    const updatedAvailability = { ...availability, [formattedDate]: [...(availability[formattedDate] || []), newSlot] };
    setAvailability(updatedAvailability);
    setStartTime("09:00");
    setEndTime("10:00");
  };

  const handleSaveAvailability = async () => {
    const targetUid = user?.id || user?.uid;
    if (!targetUid) return;

    try {
      const docRef = doc(db, "therapist_availability", targetUid);
      await setDoc(docRef, {
        monthlySlots: availability,
        updatedAt: new Date(),
        email: user?.email || "",
        name: user?.displayName || user?.name || ""
      }, { merge: true });
      onBack(); // Close after saving
    } catch (error) {
      console.error("Error saving availability:", error);
    }
  };

  const theme = {
    bg: '#8F8782',
    textBrown: '#6F4F3D',
    cardBg: '#ffffff',
    datePill: '#AA836A',
    inputBg: '#FAECE5',
  };

  const formattedDisplayDate = selectedDate.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <div style={{ ...styles.page, backgroundColor: theme.bg, minHeight: '100vh', padding: '20px', borderRadius: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

      <div style={{ width: '100%', maxWidth: '850px', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', position: 'relative', marginTop: '10px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'absolute', left: '0' }}>
            <ArrowLeftIcon color="#1a1a1a" size={24} />
          </button>
          <h2 style={{ flex: 1, textAlign: 'center', margin: 0, color: theme.textBrown, fontSize: '24px', fontWeight: 'bold' }}>
            Edit Session
          </h2>
        </div>

        <p style={{ textAlign: 'center', color: '#1a1a1a', fontSize: '15px', marginBottom: '20px', fontWeight: '600' }}>
          Click on the date to change your session:
        </p>

        {/* Date Card */}
        <div style={{ borderRadius: '20px', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', marginBottom: '15px' }}>
          <h3 style={{ color: '#1a1a1a', fontSize: '16px', fontWeight: 'bold', margin: '0 0 15px 0' }}>Change session date</h3>
          <DatePicker
            selected={selectedDate}
            onChange={setSelectedDate}
            inline
            dayClassName={getDayClassName}
          />
        </div>

        {/* Selected Date Pill */}
        <div style={{ display: 'flex', justifyContent: 'center', margin: '15px 0 25px 0' }}>
          <div style={{ backgroundColor: theme.datePill, color: '#000', padding: '10px 30px', borderRadius: '25px', fontSize: '15px', fontWeight: 'bold', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            {formattedDisplayDate}
          </div>
        </div>

        {/* Time Card */}
        <div style={{ backgroundColor: theme.cardBg, borderRadius: '20px', padding: '20px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>

          <div style={{ display: 'flex', flexDirection: 'row', gap: '30px', flexWrap: 'wrap', alignItems: 'flex-start', width: '100%' }}>

            {/* Left Column: Input */}
            <div style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px' }}>
              <h3 style={{ color: '#003049', fontSize: '16px', fontWeight: 'bold', margin: '0 0 20px 0' }}>Change session time</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', justifyContent: 'center', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CustomTimeInput label="Start Time" time={startTime} setTime={setStartTime}
                       style={{color:'#003049'}}

                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CustomTimeInput label="End Time" time={endTime} setTime={setEndTime} />
                </div>
              </div>
              <button
                onClick={addTimeSlot}
                style={{
                  backgroundColor:'#c56631', color: '#fff', border: 'none', borderRadius: '20px',
                  padding: '10px 24px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.2)', marginTop: '5px'
                }}
              >
                Add Slot
              </button>
            </div>

            {/* Right Column: Added Slots List */}
            <div style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', padding: '10px' }}>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px', fontWeight: 'bold', textAlign: 'center' }}>Added slots for this date:</p>
              {currentSlots.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto', paddingRight: '5px' }}>
                  {currentSlots.map((slot, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: theme.inputBg, padding: '10px 15px', borderRadius: '10px' }}>
                      <span style={{ fontWeight: 'bold', color: theme.textBrown, fontSize: '14px' }}>{slot.from} - {slot.to}</span>
                      <div onClick={() => removeTimeSlot(slot)} style={{ cursor: 'pointer', padding: '5px' }}>
                        <XMarkIcon size={16} color={theme.textBrown} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#aaa', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>No slots added yet.</p>
              )}
            </div>

          </div>
        </div>

        {/* Bottom Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 'auto', paddingTop: '30px', paddingBottom: '20px' }}>

          <button
            onClick={handleSaveAvailability}
            style={{
              backgroundColor: '#a35b3aed', color: '#ffffff', border: 'none', borderRadius: '25px',fontFamily:'Syne',
              padding: '15px 45px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
            }}
          >
            Save
          </button>
        </div>
      </div>

    </div>
  );
};

// --- SUB-COMPONENT: HISTORY CARD ---
const HistoryCard = ({ session, idx, safeString, getInitials }) => {
  const totalSessions = parseInt(session.sessionCount) || 1;
  const sessionTabs = Array.from({ length: totalSessions }, (_, i) => `session_${i + 1}`);

  const [activeTab, setActiveTab] = useState('session_1');

  useEffect(() => {
    // If we have any notes at all, default to the first session that has a note, 
    // otherwise default to session_1
    const firstWithNote = sessionTabs.find(tab => session.notes?.[tab]);
    setActiveTab(firstWithNote || 'session_1');
  }, [session.notes]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05 }}
      style={styles.historyCard}
    >
      <div style={styles.historyCardHeader}>
        <div style={styles.historyAvatar}>
          {getInitials(session.fullName)}
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={styles.historyClientName}>{safeString(session.fullName) || 'Unknown Client'}</h3>
          <div style={styles.historyMetaRow}>
            <div style={styles.historyMetaItem}>
              <CalendarIcon size={12} color="#C57B57" />
              <span>{safeString(session.date) || 'No Date'}</span>
            </div>
            <div style={styles.historyMetaItem}>
              <ClockIcon size={12} color="#C57B57" />
              <span>{safeString(session.sessionTime || session.timeSlot) || 'TBD'}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.historyDetailsGrid}>
        <div style={styles.historyDetailItem}>
          <UserIcon size={14} color="#666" />
          <span>{session.age ? safeString(session.age) + ' yrs' : ''} {session.gender ? ' | ' + safeString(session.gender) : ''}</span>
        </div>
        <div style={styles.historyDetailItem}>
          <PackageIcon size={14} color="#666" />
          <span>Package: {totalSessions} {totalSessions === 1 ? 'Session' : 'Sessions'}</span>
        </div>
      </div>

      <div style={styles.historyNotesSection}>
        <div style={styles.historyNotesLabel}>SESSION OBSERVATIONS</div>

        {/* Horizontal Tabs - Based on total package count */}
        <div style={styles.historyTabsWrapper}>
          {sessionTabs.map((key) => {
            const sessionNum = key.replace('session_', '');
            const isActive = activeTab === key;
            const hasNote = !!session.notes?.[key];

            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  ...styles.historyTabBtn,
                  color: isActive ? '#C57B57' : '#94A3B8',
                  borderBottom: isActive ? '2px solid #C57B57' : '2px solid transparent',
                  position: 'relative'
                }}
              >
                Session {sessionNum}
                {hasNote && <div style={{
                  position: 'absolute',
                  top: '4px',
                  right: '-2px',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: '#C57B57'
                }} />}
              </button>
            );
          })}
        </div>

        {/* Active Note Content */}
        <div style={{ marginTop: '12px', minHeight: '60px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 5 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -5 }}
              transition={{ duration: 0.2 }}
              style={styles.historyNotesText}
            >
              {session.notes?.[activeTab] ? (
                safeString(session.notes[activeTab])
              ) : (
                <span style={{ color: '#A0AEC0', fontStyle: 'italic', fontSize: '12px' }}>
                  No notes recorded for this session yet.
                </span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

// --- 2. CLIENT HISTORY ---
export const ClientHistory = ({ bookings, onBack }) => {
  const history = (bookings || []).filter(b => true);

  // Safe stringifier for rendering properties that might accidentally be objects from Firebase
  const safeString = (val) => {
    if (!val) return "";
    if (typeof val === 'object') {
      // If it's a Firestore Timestamp, try to convert to date
      if (val.toDate) return val.toDate().toLocaleDateString();
      return JSON.stringify(val);
    }
    return String(val);
  };

  const getInitials = (name) => {
    if (!name) return "??";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}><ArrowLeftIcon /></button>
        <h2 style={{ ...styles.headerTitle, fontFamily: "'Syne', sans-serif", fontWeight: '700' }}>Client History</h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {history.length > 0 ? history.map((session, idx) => (
          <HistoryCard
            key={idx}
            session={session}
            idx={idx}
            safeString={safeString}
            getInitials={getInitials}
          />
        )) : (
          <div style={styles.emptyHistory}>
            <p>No past sessions found in your history.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- 3. UPCOMING BOOKINGS ---
export const UpcomingBookings = ({ bookings, onBack, setScreen }) => {
  const getLocalDateStr = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const now = new Date();
  const todayStr = getLocalDateStr(now);

  const upcomingSessions = [];
  (bookings || []).forEach(booking => {
    const sessionCount = parseInt(booking.sessionCount) || 1;
    for (let i = 1; i <= sessionCount; i++) {
      const sessionKey = `session_${i}`;
      let session = booking[sessionKey];

      if (i === 1 && (!session || !session.date || session.date === "TBD")) {
        if (booking.date && booking.date !== "TBD") {
          session = { date: booking.date, time: booking.timeSlot, isTaken: booking.isTaken };
        }
      }

      if (session && session.date && session.date !== "TBD" && !session.isTaken && !session.isCompleted) {
        if (session.date >= todayStr) {
          upcomingSessions.push({
            ...booking,
            targetSessionNum: i,
            targetDate: session.date,
            targetTime: session.time || session.timeSlot
          });
        }
      }
    }
  });

  upcomingSessions.sort((a, b) => {
    if (a.targetDate !== b.targetDate) return a.targetDate.localeCompare(b.targetDate);
    return a.targetTime.localeCompare(b.targetTime);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}><ArrowLeftIcon /></button>
        <h2 style={{ ...styles.headerTitle, fontFamily: "'Syne', sans-serif", fontWeight: '700' }}>Upcoming Sessions</h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {upcomingSessions.length > 0 ? (
          upcomingSessions.map((item, idx) => (
            <motion.div
              key={`${item.id}-${item.targetSessionNum}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              style={{ ...styles.historyCard, padding: '20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4 style={{ ...styles.historyClientName, marginBottom: '6px' }}>{item.fullName}</h4>
                  <span style={{ fontSize: '11px', color: '#666', backgroundColor: '#F3E5DC', padding: '3px 10px', borderRadius: '6px', fontWeight: 'bold' }}>
                    Session {item.targetSessionNum} of {item.sessionCount}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '800', color: '#C57B57', fontFamily: "'Syne', sans-serif" }}>{formatDate(item.targetDate)}</div>
                  <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>{item.targetTime}</div>
                </div>
              </div>
              <div style={{ marginTop: '12px', borderTop: '1px solid #F1F2F6', paddingTop: '12px', fontSize: '12px', color: '#888' }}>
                {item.age} yrs | {item.gender}
              </div>
            </motion.div>
          ))
        ) : (
          <div style={styles.emptyState}>No upcoming sessions scheduled.</div>
        )}
      </div>
    </div>
  );
};

// --- 4. FEEDBACK / REVIEWS ---
export const FeedbackReviews = ({ onBack, therapistId, therapistEmail }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!therapistId && !therapistEmail) {
        setLoading(false);
        return;
      }
      try {
        // Primary query by email
        let q = query(
          collection(db, "session_reviews"),
          where("therapistEmail", "==", therapistEmail)
        );
        let snap = await getDocs(q);

        // If no reviews found by email, try by therapistId (fallback for old records)
        if (snap.empty && therapistId) {
          q = query(
            collection(db, "session_reviews"),
            where("therapistId", "==", therapistId)
          );
          snap = await getDocs(q);
        }

        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort by newest
        data.sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
        setReviews(data);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [therapistId]);

  const formatDate = (ts) => {
    if (!ts) return "Recently";
    const d = ts.toDate();
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleAccept = async (id) => {
    try {
      const reviewRef = doc(db, "session_reviews", id);
      await updateDoc(reviewRef, { isAccepted: true });
      setReviews(prev => prev.map(r => r.id === id ? { ...r, isAccepted: true } : r));
    } catch (err) {
      console.error("Error accepting review:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review? This action cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "session_reviews", id));
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error("Error deleting review:", err);
    }
  };

  return (
    <div style={{ ...styles.page, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: '850px', display: 'flex', flexDirection: 'column' }}>
        <div style={styles.header}>
          <button onClick={onBack} style={styles.backBtn}><ArrowLeftIcon /></button>
          <h2 style={styles.headerTitle}>Feedback & Reviews</h2>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', marginTop: '40px' }}>Loading reviews...</p>
        ) : reviews.length > 0 ? (
          <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: '10px', width: '100%' }}>
            {reviews.map(review => (
              <div key={review.id} style={{ ...styles.card, backgroundColor: 'white', padding: '20px', borderRadius: '16px', marginBottom: '15px', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 'bold', margin: 0, color: '#003049' }}>{review.userName}</h3>
                  <span style={{ fontSize: '11px', color: '#888' }}>{formatDate(review.timestamp)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                  <span style={{ fontSize: '24px' }}>
                    {["😠", "🙁", "😐", "🙂", "🤩"][review.rating - 1] || "🙂"}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#F07167', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {["Poor", "Fair", "Good", "Very Good", "Excellent"][review.rating - 1] || "Rated"}
                  </span>
                  {review.isAccepted && (
                    <span style={{ marginLeft: 'auto', fontSize: '10px', backgroundColor: '#E8F5E9', color: '#003049', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold' }}>✓ ACCEPTED</span>
                  )}
                </div>
                <p style={{ fontSize: '14px', color: '#444', lineHeight: '1.6', margin: 0 }}>
                  {review.feedback || "The patient left a positive rating without a written comment."}
                </p>
                {review.sessionNumber && (
                  <div style={{ marginTop: '10px', fontSize: '10px', color: '#A0AEC0', fontStyle: 'italic' }}>
                    Review for Session {review.sessionNumber}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '15px', marginTop: '15px', borderTop: '1px solid #F1F2F6', paddingTop: '15px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleAccept(review.id)}
                    disabled={review.isAccepted}
                    style={{
                      padding: '8px 24px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: review.isAccepted ? '#E2E8F0' : '#003049',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: review.isAccepted ? 'default' : 'pointer',
                      fontSize: '13px',
                      minWidth: '120px'
                    }}
                  >
                    {review.isAccepted ? "Accepted" : "Accept Review"}
                  </button>
                  <button
                    onClick={() => handleDelete(review.id)}
                    style={{
                      padding: '8px 24px',
                      borderRadius: '20px',
                      border: '1px solid #F07167',
                      backgroundColor: 'white',
                      color: '#F07167',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '13px',
                      minWidth: '100px'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>⭐</div>
            <p>No reviews yet. Feedback will appear here after patients complete their sessions.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- 5. BOOK APPOINTMENT ---
export const BookAppointment = ({ onBack }) => {
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}><ArrowLeftIcon /></button>
        <h2 style={styles.headerTitle}>Book Appointment</h2>
      </div>
      <div style={{ ...styles.card, backgroundColor: 'white', padding: '20px', borderRadius: '16px' }}>
        <label style={styles.label}>Client Name</label>
        <input style={{ ...styles.input, border: '1px solid #eee' }} placeholder="Enter full name" />
        <button style={styles.btn} onClick={() => alert("Booking request sent!")}>Confirm Booking</button>
      </div>
    </div>
  );
};

// --- 6. REPORT AN ISSUE ---
export const ReportIssue = ({ onBack }) => {
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}><ArrowLeftIcon /></button>
        <h2 style={styles.headerTitle}>Report an Issue</h2>
      </div>
      <div style={{ ...styles.card, backgroundColor: 'white', padding: '20px', borderRadius: '16px' }}>
        <label style={styles.label}>Description</label>
        <textarea style={{ ...styles.textArea, border: '1px solid #eee' }} placeholder="Describe the issue..."></textarea>
        <button style={styles.btn} onClick={() => alert("Issue reported.")}>Submit Report</button>
      </div>
    </div>
  );
};